// Invite message templates for the WhatsApp / Email channels of the club
// invite sheet. Kept here so copy lives in one place and can be reused by the
// backend delivery layer later. `inviterName` is optional (falls back to a
// neutral phrasing when unknown).
import {inviteUrl} from './shareInvite';

export type InviteClub = {
  clubName: string;
  code: string;
  inviterName?: string;
  visibility?: 'public' | 'private';
  joinPolicy?: 'open' | 'approval' | 'invite_only';
  minRating?: number | null;
};

// The one descriptive sentence adapts to the club's type, so we never promise
// "rating doesn't matter" on a rating-gated club or call a private club open.
function pitch(c: InviteClub): string {
  const parts: string[] = [
    "Chat with members, play together, and climb the club's activity leaderboard",
  ];
  if (c.minRating) {
    // Rating-gated club — be upfront about the bar instead of "rating doesn't matter".
    parts.push(`. This club is for players rated ${c.minRating}+`);
  } else {
    parts.push(" — rating doesn't matter here");
  }
  if (c.joinPolicy === 'invite_only' || c.visibility === 'private') {
    parts.push(". It's a private, invite-only club");
  } else if (c.joinPolicy === 'approval') {
    parts.push('. An admin will approve your request to join');
  }
  return parts.join('') + '.';
}

// WhatsApp is delivered server-side via an APPROVED WhatsApp Business template
// registered in the Meta dashboard (free-text can't be sent to users who
// haven't messaged first). Register this template, then the backend calls the
// Meta API with `name: WA_TEMPLATE.name` and body params in this exact order.
export const WA_TEMPLATE = {
  name: 'caissa_club_invite',
  language: 'en',
  // Approved body text with positional variables (as registered in Meta):
  //   ♟ {{1}} to join {{2}} on Caissa! {{3}} Tap to join (valid 24h): {{4}}
  // {{1}} who · {{2}} clubName · {{3}} pitch sentence · {{4}} invite URL
  body: '♟ {{1}} to join {{2}} on Caissa!\n\n{{3}}\n\nTap to join (link valid 24h): {{4}}',
};

// Ordered body params for WA_TEMPLATE — hand this array to the Meta send call.
export function whatsappTemplateParams(c: InviteClub): string[] {
  const who = c.inviterName ? `${c.inviterName} invited you` : "You're invited";
  return [who, c.clubName, pitch(c), inviteUrl(c.code)];
}

// Plain-text rendering of the same message — used for the in-sheet preview so
// the admin sees what the invitee will receive.
export function whatsappInviteText(c: InviteClub): string {
  const [who, name, body, url] = whatsappTemplateParams(c);
  return `♟ ${who} to join *${name}* on Caissa!\n\n${body}\n\nTap to join (link valid 24h):\n${url}`;
}

export function emailInvite(c: InviteClub & {note?: string}): {
  subject: string;
  body: string;
  preheader: string;
} {
  const who = c.inviterName
    ? `${c.inviterName} invited you`
    : "You've been invited";
  const subject = c.inviterName
    ? `${c.inviterName} invited you to ${c.clubName} on Caissa`
    : `You're invited to join ${c.clubName} on Caissa`;
  const preheader = c.minRating
    ? `Join ${c.clubName} (rated ${c.minRating}+) and compete on activity leaderboards.`
    : `Join ${c.clubName}, chat, and compete on activity leaderboards.`;
  const body =
    'Hi,\n\n' +
    `${who} to join ${c.clubName} on Caissa.\n\n` +
    (c.note ? `“${c.note}”\n\n` : '') +
    `${pitch(c)}\n\n` +
    `Accept your invite (valid for 24 hours):\n${inviteUrl(c.code)}\n\n` +
    'See you in the club,\nThe Caissa Team';
  return {subject, body, preheader};
}
